import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { RefreshCw, Search, Download } from 'lucide-react';
import { playerProfileService } from '../apis/playerProfileService';

const DEFAULT_LIMIT = 50;

const REASON_LABELS = {
  invalid_format: 'Invalid format',
  invalid_number: 'Invalid / undeliverable',
  unsubscribed: 'Unsubscribed',
};

const currentLoungeLabel = () => {
  if (typeof window === 'undefined') return 'Brownsville';
  const host = window.location.hostname.toLowerCase();
  if (host.includes('mcallen') && !host.includes('brownsville')) return 'McAllen';
  return 'Brownsville';
};

const logStatus = (row) => {
  if (row?.success) return 'sent';
  if (row?.twilio_status === 'skipped') return 'skipped';
  return 'failed';
};

const statusBadge = (status) => {
  if (status === 'sent') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Sent
      </span>
    );
  }
  if (status === 'skipped') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
        Skipped
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
      Failed
    </span>
  );
};

const reasonBadge = (reason) => {
  const label = REASON_LABELS[reason] || reason || 'Unknown';
  const cls =
    reason === 'unsubscribed'
      ? 'bg-gray-100 text-gray-800'
      : reason === 'invalid_format'
        ? 'bg-amber-100 text-amber-800'
        : 'bg-red-100 text-red-800';
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
};

const escapeCsv = (v) => {
  if (v == null) return '';
  const s = String(v).replace(/"/g, '""');
  return /[,"\n\r]/.test(s) ? `"${s}"` : s;
};

const downloadCsv = (filename, headers, rows) => {
  const csv = '\uFEFF' + [headers.join(','), ...rows].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const Pagination = ({ page, totalPages, total, loading, onPage }) => (
  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
    <div className="text-sm text-gray-600">
      Total: <span className="font-medium">{total}</span>
    </div>
    <div className="flex items-center gap-2">
      <button
        onClick={() => onPage(Math.max(1, page - 1))}
        disabled={page <= 1 || loading}
        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        Prev
      </button>
      <div className="text-sm text-gray-700">
        Page <span className="font-medium">{page}</span> / <span className="font-medium">{totalPages}</span>
      </div>
      <button
        onClick={() => onPage(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages || loading}
        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        Next
      </button>
    </div>
  </div>
);

const SmsLogsPage = () => {
  const lounge = useMemo(() => currentLoungeLabel(), []);
  const [tab, setTab] = useState('activity');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({ sent: 0, skipped: 0, failed: 0, total: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [invalidRows, setInvalidRows] = useState([]);
  const [invalidLoading, setInvalidLoading] = useState(false);
  const [invalidError, setInvalidError] = useState(null);
  const [invalidPage, setInvalidPage] = useState(1);
  const [invalidLimit, setInvalidLimit] = useState(DEFAULT_LIMIT);
  const [invalidTotalPages, setInvalidTotalPages] = useState(1);
  const [invalidTotal, setInvalidTotal] = useState(0);
  const [invalidSummary, setInvalidSummary] = useState({ unique_numbers: 0, total_hits: 0, by_reason: {} });
  const [invalidSearch, setInvalidSearch] = useState('');
  const [invalidReason, setInvalidReason] = useState('all');
  const [invalidSort, setInvalidSort] = useState('newest');
  const [invalidFrom, setInvalidFrom] = useState('');
  const [invalidTo, setInvalidTo] = useState('');

  const logQuery = useMemo(() => {
    const p = { page, limit, sort };
    if (search.trim()) p.search = search.trim();
    if (statusFilter === 'sent') p.status = 'sent';
    if (statusFilter === 'skipped') p.status = 'skipped';
    if (statusFilter === 'failed') p.status = 'failed';
    if (dateFrom) p.from = `${dateFrom}T00:00:00.000`;
    if (dateTo) p.to = `${dateTo}T23:59:59.999`;
    return p;
  }, [page, limit, sort, search, statusFilter, dateFrom, dateTo]);

  const invalidQuery = useMemo(() => {
    const p = { page: invalidPage, limit: invalidLimit, sort: invalidSort };
    if (invalidSearch.trim()) p.search = invalidSearch.trim();
    if (invalidReason !== 'all') p.reason = invalidReason;
    if (invalidFrom) p.from = `${invalidFrom}T00:00:00.000`;
    if (invalidTo) p.to = `${invalidTo}T23:59:59.999`;
    return p;
  }, [invalidPage, invalidLimit, invalidSort, invalidSearch, invalidReason, invalidFrom, invalidTo]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await playerProfileService.getSmsLogs(logQuery);
      if (!response?.success) {
        setError(response?.error || 'Failed to load SMS logs');
        setRows([]);
        return;
      }
      const data = response?.data?.data || {};
      setRows(data.rows || []);
      const pagination = data.pagination || {};
      setTotal(pagination.total || 0);
      setTotalPages(pagination.totalPages || 1);
      setSummary({
        sent: data.summary?.sent || 0,
        skipped: data.summary?.skipped || 0,
        failed: data.summary?.failed || 0,
        total: data.summary?.total || pagination.total || 0,
      });
    } catch (e) {
      setError(e?.message || 'Failed to load SMS logs');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [logQuery]);

  const fetchInvalid = useCallback(async () => {
    setInvalidLoading(true);
    setInvalidError(null);
    try {
      const response = await playerProfileService.getSmsInvalidPhones(invalidQuery);
      if (!response?.success) {
        setInvalidError(response?.error || 'Failed to load invalid numbers');
        setInvalidRows([]);
        return;
      }
      const data = response?.data?.data || {};
      setInvalidRows(data.rows || []);
      const pagination = data.pagination || {};
      setInvalidTotal(pagination.total || 0);
      setInvalidTotalPages(pagination.totalPages || 1);
      setInvalidSummary({
        unique_numbers: data.summary?.unique_numbers || 0,
        total_hits: data.summary?.total_hits || 0,
        by_reason: data.summary?.by_reason || {},
      });
    } catch (e) {
      setInvalidError(e?.message || 'Failed to load invalid numbers');
      setInvalidRows([]);
    } finally {
      setInvalidLoading(false);
    }
  }, [invalidQuery]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchInvalid();
  }, [fetchInvalid]);

  const downloadLogReport = useCallback(async () => {
    try {
      const response = await playerProfileService.getSmsLogs({ ...logQuery, page: 1, limit: 5000, export: '1' });
      if (!response?.success) return;
      const exportRows = response?.data?.data?.rows || [];
      downloadCsv(
        `sms-logs-${moment().format('YYYY-MM-DD-HHmm')}.csv`,
        ['Time', 'Player Name', 'Player Number', 'Player ID', 'Phone', 'Status', 'Sent By', 'Message', 'Error'],
        exportRows.map((row) =>
          [
            row.created_at ? moment(row.created_at).format('YYYY-MM-DD HH:mm:ss') : '',
            escapeCsv(row.player_name),
            escapeCsv(row.player_number),
            row.player_id != null ? row.player_id : '',
            escapeCsv(row.phone),
            logStatus(row) === 'sent' ? 'Sent' : logStatus(row) === 'skipped' ? 'Skipped' : 'Failed',
            escapeCsv(row.sent_by_email),
            escapeCsv(row.message),
            escapeCsv(row.error),
          ].join(',')
        )
      );
    } catch (e) {
      console.error('Download failed:', e);
    }
  }, [logQuery]);

  const downloadInvalidReport = useCallback(async () => {
    try {
      const response = await playerProfileService.getSmsInvalidPhones({
        ...invalidQuery,
        page: 1,
        limit: 5000,
        export: '1',
      });
      if (!response?.success) return;
      const exportRows = response?.data?.data?.rows || [];
      downloadCsv(
        `sms-invalid-phones-${moment().format('YYYY-MM-DD-HHmm')}.csv`,
        ['Phone', 'Original', 'Player Name', 'Player Number', 'Reason', 'Error', 'Times rejected', 'First seen', 'Last seen', 'Last sent by'],
        exportRows.map((row) =>
          [
            escapeCsv(row.phone),
            escapeCsv(row.original_phone),
            escapeCsv(row.player_name),
            escapeCsv(row.player_number),
            escapeCsv(REASON_LABELS[row.reason] || row.reason),
            escapeCsv(row.error_message),
            row.hit_count || 0,
            row.first_seen_at ? moment(row.first_seen_at).format('YYYY-MM-DD HH:mm:ss') : '',
            row.last_seen_at ? moment(row.last_seen_at).format('YYYY-MM-DD HH:mm:ss') : '',
            escapeCsv(row.last_sent_by_email),
          ].join(',')
        )
      );
    } catch (e) {
      console.error('Download failed:', e);
    }
  }, [invalidQuery]);

  const summaryCards = [
    { key: 'sent', label: 'Sent', value: summary.sent, active: 'bg-green-50 border-green-200 text-green-800', idle: 'bg-white' },
    { key: 'skipped', label: 'Skipped / invalid', value: summary.skipped, active: 'bg-amber-50 border-amber-200 text-amber-800', idle: 'bg-white' },
    { key: 'failed', label: 'Failed', value: summary.failed, active: 'bg-red-50 border-red-200 text-red-800', idle: 'bg-white' },
    { key: 'all', label: 'Total attempts', value: summary.total, active: 'bg-blue-50 border-blue-200 text-blue-800', idle: 'bg-white' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="bg-white shadow-sm border-b border-gray-200 rounded-lg p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">SMS Logs</h1>
            <p className="text-sm text-gray-500 mt-1">
              {lounge} — sent vs skipped vs failed, and unique invalid numbers.
              Newest first by default; switch sort to review older activity.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={tab === 'invalid' ? downloadInvalidReport : downloadLogReport}
              disabled={tab === 'invalid' ? invalidLoading : loading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <Download size={16} />
              Download report
            </button>
            <button
              onClick={tab === 'invalid' ? fetchInvalid : fetchLogs}
              disabled={tab === 'invalid' ? invalidLoading : loading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <RefreshCw size={16} className={(tab === 'invalid' ? invalidLoading : loading) ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => setTab('activity')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              tab === 'activity' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Message activity
          </button>
          <button
            type="button"
            onClick={() => setTab('invalid')}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              tab === 'invalid' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Invalid numbers
            {invalidSummary.unique_numbers > 0 ? ` (${invalidSummary.unique_numbers})` : ''}
          </button>
        </div>
      </div>

      {tab === 'activity' ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {summaryCards.map((card) => (
              <button
                key={card.key}
                type="button"
                onClick={() => {
                  setStatusFilter(card.key);
                  setPage(1);
                }}
                className={`text-left border rounded-lg p-4 ${
                  statusFilter === card.key ? card.active : `${card.idle} border-gray-200 hover:border-gray-300`
                }`}
              >
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500">{card.label}</p>
                <p className="text-2xl font-bold mt-1">{card.value}</p>
              </button>
            ))}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex flex-col lg:flex-row gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search by phone, player, sent by, message..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
              />
              <input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value);
                  setPage(1);
                }}
                min={dateFrom || undefined}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
              />
              <button
                type="button"
                onClick={() => {
                  setDateFrom('');
                  setDateTo('');
                  setPage(1);
                }}
                className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Clear dates
              </button>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
              >
                <option value="all">All statuses</option>
                <option value="sent">Sent</option>
                <option value="skipped">Skipped / invalid</option>
                <option value="failed">Failed</option>
              </select>
              <select
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value);
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value, 10));
                  setPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
              >
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>

            {error && <div className="px-4 py-3 text-sm text-red-600">{error}</div>}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Player</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Sent By</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Message</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading && rows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-10 text-center text-sm text-gray-500">No SMS logs found.</td>
                    </tr>
                  ) : (
                    rows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {row.created_at ? moment(row.created_at).format('MMM DD, YYYY h:mm A') : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="font-medium">{row.player_name || '—'}</div>
                          <div className="text-xs text-gray-500">
                            {row.player_number ? `#${row.player_number}` : ''}
                            {row.player_id ? ` • ID ${row.player_id}` : ''}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{row.phone}</td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">{statusBadge(logStatus(row))}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{row.sent_by_email || '—'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-[520px]">
                          <div className="line-clamp-2 whitespace-pre-wrap">{row.message || ''}</div>
                          {!row.success && row.error && (
                            <div className="mt-1 text-xs text-red-600 line-clamp-2">{row.error}</div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination page={page} totalPages={totalPages} total={total} loading={loading} onPage={setPage} />
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div className="border border-gray-200 rounded-lg p-4 bg-white">
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Unique numbers</p>
              <p className="text-2xl font-bold mt-1">{invalidSummary.unique_numbers}</p>
            </div>
            <div className="border border-amber-200 rounded-lg p-4 bg-amber-50">
              <p className="text-xs font-medium uppercase tracking-wider text-amber-700">Times skipped</p>
              <p className="text-2xl font-bold mt-1 text-amber-800">{invalidSummary.total_hits}</p>
            </div>
            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <p className="text-xs font-medium uppercase tracking-wider text-red-700">Invalid / undeliverable</p>
              <p className="text-2xl font-bold mt-1 text-red-800">
                {(invalidSummary.by_reason.invalid_number || 0) + (invalidSummary.by_reason.invalid_format || 0)}
              </p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500">Unsubscribed</p>
              <p className="text-2xl font-bold mt-1">{invalidSummary.by_reason.unsubscribed || 0}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <p className="text-sm text-gray-600 mb-3">
                Each phone is stored once. Last seen and times rejected update when SMS is skipped again — use this list to fix player records.
              </p>
              <div className="flex flex-col lg:flex-row gap-3 flex-wrap">
                <div className="flex-1 min-w-[200px] relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    value={invalidSearch}
                    onChange={(e) => {
                      setInvalidSearch(e.target.value);
                      setInvalidPage(1);
                    }}
                    placeholder="Search phone, player, or rejection reason..."
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <input
                  type="date"
                  value={invalidFrom}
                  onChange={(e) => {
                    setInvalidFrom(e.target.value);
                    setInvalidPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                />
                <input
                  type="date"
                  value={invalidTo}
                  onChange={(e) => {
                    setInvalidTo(e.target.value);
                    setInvalidPage(1);
                  }}
                  min={invalidFrom || undefined}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                />
                <select
                  value={invalidReason}
                  onChange={(e) => {
                    setInvalidReason(e.target.value);
                    setInvalidPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                >
                  <option value="all">All reasons</option>
                  <option value="invalid_format">Invalid format</option>
                  <option value="invalid_number">Invalid / undeliverable</option>
                  <option value="unsubscribed">Unsubscribed</option>
                </select>
                <select
                  value={invalidSort}
                  onChange={(e) => {
                    setInvalidSort(e.target.value);
                    setInvalidPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                >
                  <option value="newest">Newest last seen</option>
                  <option value="oldest">Oldest first seen</option>
                </select>
                <select
                  value={invalidLimit}
                  onChange={(e) => {
                    setInvalidLimit(parseInt(e.target.value, 10));
                    setInvalidPage(1);
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                >
                  <option value={25}>25 / page</option>
                  <option value={50}>50 / page</option>
                  <option value={100}>100 / page</option>
                </select>
              </div>
            </div>

            {invalidError && <div className="px-4 py-3 text-sm text-red-600">{invalidError}</div>}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Player</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Reason</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Times rejected</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">First seen</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Last seen</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invalidLoading && invalidRows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td>
                    </tr>
                  ) : invalidRows.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-10 text-center text-sm text-gray-500">
                        No invalid or unusable numbers captured yet.
                      </td>
                    </tr>
                  ) : (
                    invalidRows.map((row) => (
                      <tr key={row.id || row.phone} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          <div className="font-medium">{row.phone}</div>
                          {row.original_phone && row.original_phone !== row.phone && (
                            <div className="text-xs text-gray-500">{row.original_phone}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="font-medium">{row.player_name || '—'}</div>
                          <div className="text-xs text-gray-500">
                            {row.player_number ? `#${row.player_number}` : ''}
                            {row.player_id ? ` • ID ${row.player_id}` : ''}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {reasonBadge(row.reason)}
                          {row.error_message && (
                            <div className="mt-1 text-xs text-gray-500 max-w-[280px] line-clamp-2">{row.error_message}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-800">{row.hit_count || 1}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {row.first_seen_at ? moment(row.first_seen_at).format('MMM DD, YYYY h:mm A') : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                          {row.last_seen_at ? moment(row.last_seen_at).format('MMM DD, YYYY h:mm A') : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              page={invalidPage}
              totalPages={invalidTotalPages}
              total={invalidTotal}
              loading={invalidLoading}
              onPage={setInvalidPage}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default SmsLogsPage;
